import { PickerItem, SgPicker } from '@sgme/ui';
import { Badge } from 'components/common-form/builder/form/node/field/MultipleBadgeField';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import { isEqual } from 'lodash-es';
import { MultiSelectFieldData, MultiSelectFieldMetadata } from 'models/forms/fields/MultiSelectFieldMetadata';
import { useCallback } from 'react';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export function MultiSelectField({ node: { id }, workspaceType }: ProductNodeBuilderProps) {
  const [data, setData] = useData<MultiSelectFieldData>(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  const metadata = useFieldMetadata<MultiSelectFieldMetadata>(id)!;
  const { formatMessage } = useSPIntl();
  const { isReadOnly, availableItems, translationKey } = metadata;

  const onSelection = useCallback(
    (selections: readonly string[]) => {
      if (isEqual(data, selections)) {
        return;
      }
      setData(selections);
    },
    [data, setData],
  );

  if (isReadOnly) {
    return (
      <BlockOrientationFormGroup
        inputId={id}
        label={`form.${translationKey}` as IntlKey}
        workspaceType={workspaceType}
        className={patchedFieldClassName}
      >
        {availableItems?.map(item => {
          return (
            <Badge
              key={item.value}
              label={item.label === undefined ? item.value : formatMessage({ id: item.label as IntlKey })}
              e2e={`badge-${id}`}
            />
          );
        })}
      </BlockOrientationFormGroup>
    );
  }

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as IntlKey}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <SgPicker
        id={id}
        onChange={onSelection}
        value={data ? [...data] : []}
        multiSelect
        items={availableItems.map(i => ({ key: i.value, label: i.label }) satisfies PickerItem<string>)}
        showIcon={false}
        showClearButton={false}
        placeholder={formatMessage({ id: 'form.picker.placeholder' })}
      />
    </BlockOrientationFormGroup>
  );
}
